import type { ComponentProps, Ref } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  Input,
  InputHelperText,
  InputLabel,
  InputRoot,
} from '@mint-lib/tw-components';
import { TextArea } from '@mint-lib/tw-components/patterns';

type InputFieldProps = Omit<ComponentProps<typeof Input>, 'name' | 'color'> & {
  name: string;
  label?: string;
  infoText?: string;
  error?: boolean;
  helperText?: string;
  multiline?: boolean;
  rows?: number;
};

const InputField = ({
  name,
  label,
  infoText,
  error,
  helperText,
  id,
  disabled,
  multiline,
  rows,
  onChange,
  ...otherProps
}: InputFieldProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const fieldId = id ?? name;
  const message = helperText ?? (errors[name]?.message as string | undefined);
  const hasError = error ?? !!errors[name];
  const color = hasError ? 'error' : 'default';

  return (
    <Controller
      control={control}
      name={name}
      defaultValue=""
      render={({ field: { ref: fieldRef, ...field } }) => {
        const handleChange = (
          e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => {
          field.onChange(e);
          onChange?.(e as React.ChangeEvent<HTMLInputElement>);
        };

        return multiline ? (
          <TextArea
            {...field}
            {...(otherProps as ComponentProps<typeof TextArea>)}
            ref={fieldRef as Ref<HTMLTextAreaElement>}
            id={fieldId}
            onChange={handleChange}
            rows={rows}
            style={
              rows ? { minHeight: `calc(${rows} * 1rem + 1rem)` } : undefined
            }
            label={label}
            infoIconWithTooltip={infoText}
            helperText={message}
            disabled={disabled}
            color={color}
          />
        ) : (
          <InputRoot className="w-full">
            {label ? (
              <InputLabel
                label={label}
                htmlFor={fieldId}
                infoIconWithTooltip={infoText}
              />
            ) : null}
            <Input
              {...field}
              {...otherProps}
              ref={fieldRef}
              id={fieldId}
              onChange={handleChange}
              disabled={disabled}
              color={color}
            />
            {message ? (
              <InputHelperText color={color} helperText={message} />
            ) : null}
          </InputRoot>
        );
      }}
    />
  );
};

export default InputField;
